/**
 * Download Manager for handling CSV file downloads
 * Manages secure file downloads from Firebase Storage
 */

import { ref, getDownloadURL } from 'firebase/storage';
import { storage, isUserAuthenticated, getCurrentUser } from '../firebase/config';
import { TRANSACTION_STATUS } from '../hooks/useTransactions';
import { 
  trackFileDownloadStarted,
  trackFileDownloadCompleted,
  trackFileDownloadError,
  trackError 
} from './analytics';

class DownloadManager {
  constructor() {
    this.activeDownloads = new Map();
  }

  /**
   * Download a CSV file from a completed transaction
   * @param {Object} transaction - Transaction object with downloadUrl
   * @param {Function} onProgress - Progress callback function
   * @param {Function} onStatusUpdate - Status update callback
   * @returns {Promise<boolean>} Success status
   */
  async downloadCSV(transaction, onProgress = null, onStatusUpdate = null) {
    const { id: transactionId, fileName, downloadUrl, csvStoragePath, fileSize, creditsUsed, userId } = transaction;
    const downloadStartTime = Date.now();

    try {
      // Pre-download authentication validation
      const authValidation = await this.validateAuthForDownload(transaction);
      
      if (!authValidation.valid) {
        throw new Error(authValidation.error);
      }
      
      // Progress tracking - auth validated
      if (onProgress) onProgress(10);
      
      // Track download started
      trackFileDownloadStarted({
        transactionId,
        fileName,
        fileType: 'csv',
        fileSize: fileSize || 0,
        downloadSource: downloadUrl ? 'direct_url' : 'storage_path'
      });

      // Update status to downloading
      if (onStatusUpdate) {
        await onStatusUpdate(transactionId, TRANSACTION_STATUS.DOWNLOADING);
      }

      // Track this download
      this.activeDownloads.set(transactionId, { status: 'downloading', progress: 10 });
      if (onProgress) onProgress(20);

      // FORCE regeneration of download URL to ensure fresh auth token is used
      // Don't trust the stored downloadUrl as it might be tied to a different auth context
      let finalDownloadUrl = null;

      // Always regenerate download URL from storage path if available
      if (csvStoragePath) {
        try {
          // Force refresh auth token before getting download URL
          const currentUser = await getCurrentUser();
          if (currentUser) {
            await currentUser.getIdToken(true); // Force refresh
          }
          
          const storageRef = ref(storage, csvStoragePath);
          finalDownloadUrl = await getDownloadURL(storageRef);
          
        } catch (storageError) {
          console.error('Storage access failed:', storageError);
          
          // Check if it's an auth-related error
          if (storageError.code === 'storage/unauthorized' || 
              storageError.code === 'storage/unauthenticated') {
            throw new Error('Your session has expired. Please refresh the page and log in again, then try downloading.');
          }
          
          throw new Error(`Storage access failed: ${storageError.message}`);
        }
      }

      // Fallback to stored downloadUrl only if no csvStoragePath available
      if (!finalDownloadUrl && downloadUrl) {
        finalDownloadUrl = downloadUrl;
      }

      if (!finalDownloadUrl) {
        throw new Error('No download URL available for this file');
      }

      // Progress tracking
      if (onProgress) onProgress(40);
      this.activeDownloads.set(transactionId, { status: 'downloading', progress: 40 });

      // Optional URL validation (skip if CORS issues)
      try {
        const response = await fetch(finalDownloadUrl, { 
          method: 'HEAD',
          mode: 'cors'
        });
        
        if (!response.ok && (response.status === 401 || response.status === 403)) {
          throw new Error('Access denied. Please log in again and try downloading.');
        }
      } catch (headError) {
        // Continue with download even if HEAD request fails (common with CORS)
        if (headError.message.includes('Access denied')) {
          throw headError;
        }
      }

      if (onProgress) onProgress(70);
      this.activeDownloads.set(transactionId, { status: 'downloading', progress: 70 });

      // Create download with user-friendly filename
      const csvFileName = this.generateCSVFileName(fileName);
      await this.triggerDownload(finalDownloadUrl, csvFileName);

      if (onProgress) onProgress(100);

      // Update download status
      this.activeDownloads.set(transactionId, { status: 'completed', progress: 100 });
      
      // Track successful download
      const downloadTime = Math.floor((Date.now() - downloadStartTime) / 1000);
      trackFileDownloadCompleted({
        transactionId,
        fileName: csvFileName,
        fileType: 'csv',
        downloadTimeSeconds: downloadTime,
        fileSizeBytes: fileSize || 0,
        creditsUsed: creditsUsed || 0
      });
      
      return true;

    } catch (error) {
      console.error('Download error:', error);
      
      // Track download error
      trackFileDownloadError({
        transactionId,
        fileName,
        errorType: 'download_failed',
        errorMessage: error.message || 'Download failed',
        downloadSource: downloadUrl ? 'direct_url' : 'storage_path'
      });
      
      // Update error status
      this.activeDownloads.set(transactionId, { 
        status: 'error', 
        error: error.message 
      });

      // Update transaction status back to completed (so user can retry)
      if (onStatusUpdate) {
        await onStatusUpdate(transactionId, TRANSACTION_STATUS.COMPLETED, {
          downloadError: error.message
        });
      }

      throw error;
    } finally {
      // Clean up tracking after delay
      setTimeout(() => {
        this.activeDownloads.delete(transactionId);
      }, 5000);
    }
  }

  /**
   * Download an Excel file from a completed transaction
   * @param {Object} transaction - Transaction object with rawDownloadUrl
   * @param {Function} onProgress - Progress callback function
   * @param {Function} onStatusUpdate - Status update callback
   * @returns {Promise<boolean>} Success status
   */
  async downloadExcel(transaction, onProgress = null, onStatusUpdate = null) {
    const { id: transactionId, fileName, rawDownloadUrl, rawExcelStoragePath, fileSize, creditsUsed, userId } = transaction;
    const downloadStartTime = Date.now();

    try {
      // Pre-download authentication validation
      const authValidation = await this.validateAuthForDownload(transaction);
      
      if (!authValidation.valid) {
        throw new Error(authValidation.error);
      }
      
      // Progress tracking - auth validated
      if (onProgress) onProgress(10);
      
      // Track download started
      trackFileDownloadStarted({
        transactionId,
        fileName,
        fileType: 'excel',
        fileSize: fileSize || 0,
        downloadSource: rawDownloadUrl ? 'direct_url' : 'storage_path'
      });

      // Update status to downloading
      if (onStatusUpdate) {
        await onStatusUpdate(transactionId, TRANSACTION_STATUS.DOWNLOADING);
      }

      // Track this download
      this.activeDownloads.set(transactionId, { status: 'downloading', progress: 10 });
      if (onProgress) onProgress(20);

      // FORCE regeneration of download URL to ensure fresh auth token is used
      let finalDownloadUrl = null;

      // Always regenerate download URL from storage path if available
      if (rawExcelStoragePath) {
        try {
          // Force refresh auth token before getting download URL
          const currentUser = await getCurrentUser();
          if (currentUser) {
            await currentUser.getIdToken(true); // Force refresh
          }
          
          const storageRef = ref(storage, rawExcelStoragePath);
          finalDownloadUrl = await getDownloadURL(storageRef);
          
        } catch (storageError) {
          console.error('Storage access failed:', storageError);
          
          // Check if it's an auth-related error
          if (storageError.code === 'storage/unauthorized' || 
              storageError.code === 'storage/unauthenticated') {
            throw new Error('Your session has expired. Please refresh the page and log in again, then try downloading.');
          }
          
          throw new Error(`Storage access failed: ${storageError.message}`);
        }
      }

      // Fallback to stored rawDownloadUrl only if no rawExcelStoragePath available
      if (!finalDownloadUrl && rawDownloadUrl) {
        finalDownloadUrl = rawDownloadUrl;
      }

      if (!finalDownloadUrl) {
        throw new Error('No Excel download URL available for this file');
      }

      // Progress tracking
      if (onProgress) onProgress(40);
      this.activeDownloads.set(transactionId, { status: 'downloading', progress: 40 });

      // Optional URL validation (skip if CORS issues)
      try {
        const response = await fetch(finalDownloadUrl, { 
          method: 'HEAD',
          mode: 'cors'
        });
        
        if (!response.ok && (response.status === 401 || response.status === 403)) {
          throw new Error('Access denied. Please log in again and try downloading.');
        }
      } catch (headError) {
        // Continue with download even if HEAD request fails (common with CORS)
        if (headError.message.includes('Access denied')) {
          throw headError;
        }
      }

      if (onProgress) onProgress(70);
      this.activeDownloads.set(transactionId, { status: 'downloading', progress: 70 });

      // Create download with user-friendly filename
      const excelFileName = this.generateExcelFileName(fileName);
      await this.triggerDownload(finalDownloadUrl, excelFileName);

      if (onProgress) onProgress(100);

      // Update download status
      this.activeDownloads.set(transactionId, { status: 'completed', progress: 100 });
      
      // Track successful download
      const downloadTime = Math.floor((Date.now() - downloadStartTime) / 1000);
      trackFileDownloadCompleted({
        transactionId,
        fileName: excelFileName,
        fileType: 'excel',
        downloadTimeSeconds: downloadTime,
        fileSizeBytes: fileSize || 0,
        creditsUsed: creditsUsed || 0
      });
      
      return true;

    } catch (error) {
      console.error('Excel download error:', error);
      
      // Track download error
      trackFileDownloadError({
        transactionId,
        fileName,
        errorType: 'download_failed',
        errorMessage: error.message || 'Excel download failed',
        downloadSource: rawDownloadUrl ? 'direct_url' : 'storage_path'
      });
      
      // Update error status
      this.activeDownloads.set(transactionId, { 
        status: 'error', 
        error: error.message 
      });

      // Update transaction status back to completed (so user can retry)
      if (onStatusUpdate) {
        await onStatusUpdate(transactionId, TRANSACTION_STATUS.COMPLETED, {
          downloadError: error.message
        });
      }

      // Re-throw error for caller handling
      throw error;
    } finally {
      // Clean up tracking after delay
      setTimeout(() => {
        this.activeDownloads.delete(transactionId);
      }, 5000);
    }
  }

  /**
   * Generate user-friendly CSV filename from original PDF name
   * Format: PDF_Name_YYYY-MM-DD.csv
   * @param {string} pdfFileName - Original PDF filename
   * @returns {string} User-friendly CSV filename
   */
  generateCSVFileName(pdfFileName) {
    // Get today's date in YYYY-MM-DD format
    const today = new Date().toISOString().split('T')[0];
    
    if (!pdfFileName) {
      return `Bank_Statement_${today}.csv`;
    }

    // Extract base name from PDF (remove .pdf extension)
    const baseName = pdfFileName
      .replace(/\.pdf$/i, '')
      .replace(/\.PDF$/i, '')
      .trim();
    
    // Clean up the name for better file compatibility
    const cleanName = baseName
      .replace(/[^a-zA-Z0-9_\-\s]/g, '') // Remove special chars except underscore, dash, space
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .replace(/_+/g, '_') // Replace multiple underscores with single
      .replace(/^_+|_+$/g, '') // Remove leading/trailing underscores
      .substring(0, 50); // Limit length to avoid filesystem issues
    
    // Handle edge case where cleaning results in empty string
    const finalBaseName = cleanName || 'Bank_Statement';
    
    return `${finalBaseName}_${today}.csv`;
  }

  /**
   * Generate user-friendly Excel filename from original PDF name
   * Format: PDF_Name_unformatted_YYYY-MM-DD.xlsx
   * @param {string} pdfFileName - Original PDF filename
   * @returns {string} User-friendly Excel filename
   */
  generateExcelFileName(pdfFileName) {
    // Get today's date in YYYY-MM-DD format
    const today = new Date().toISOString().split('T')[0];
    
    if (!pdfFileName) {
      return `Bank_Statement_unformatted_${today}.xlsx`;
    }
    
    // Remove .pdf extension and clean the base name
    const baseName = pdfFileName.replace(/\.pdf$/i, '');
    const cleanName = baseName
      .replace(/[^a-zA-Z0-9_\-\s]/g, '') // Remove special chars except underscore, dash, space
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .replace(/_+/g, '_') // Replace multiple underscores with single
      .replace(/^_+|_+$/g, '') // Remove leading/trailing underscores
      .substring(0, 45); // Limit length (shorter to accommodate "_unformatted")
    
    // Handle edge case where cleaning results in empty string
    const finalBaseName = cleanName || 'Bank_Statement';
    
    return `${finalBaseName}_unformatted_${today}.xlsx`;
  }

  /**
   * Trigger browser download of file from URL
   * @param {string} url - File download URL
   * @param {string} filename - Download filename
   */
  async triggerDownload(url, filename) {
    try {
      // Fetch the file content to create a blob (fixes CORS origin issue)
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      // Create a blob URL (same origin)
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create temporary download link
      const downloadLink = document.createElement('a');
      downloadLink.href = blobUrl;
      downloadLink.download = filename;
      downloadLink.style.display = 'none';

      // Add to DOM, click, and remove
      document.body.appendChild(downloadLink);
      downloadLink.click();
      
      // Clean up blob URL and DOM element
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
        document.body.removeChild(downloadLink);
      }, 100);

    } catch (error) {
      console.error('Download trigger error:', error);
      throw error;
    }
  }

  /**
   * Get download status for a transaction
   * @param {string} transactionId - Transaction ID
   * @returns {Object|null} Download status
   */
  getDownloadStatus(transactionId) {
    return this.activeDownloads.get(transactionId) || null;
  }

  /**
   * Check if a download is currently active
   * @param {string} transactionId - Transaction ID
   * @returns {boolean} Is downloading
   */
  isDownloading(transactionId) {
    const status = this.activeDownloads.get(transactionId);
    return status?.status === 'downloading';
  }

  /**
   * Cancel an active download
   * @param {string} transactionId - Transaction ID
   */
  cancelDownload(transactionId) {
    this.activeDownloads.delete(transactionId);
  }

  /**
   * Validate if transaction is ready for download
   * @param {Object} transaction - Transaction object
   * @returns {Object} Validation result
   */
  validateDownload(transaction) {
    if (!transaction) {
      return { valid: false, error: 'Transaction not found' };
    }

    if (transaction.status !== TRANSACTION_STATUS.COMPLETED) {
      return { valid: false, error: 'File is not ready for download' };
    }

    if (!transaction.downloadUrl && !transaction.csvStoragePath) {
      return { valid: false, error: 'Download URL not available' };
    }

    return { valid: true };
  }

  /**
   * Preview CSV content (first few lines)
   * @param {Object} transaction - Transaction object
   * @returns {Promise<string>} Preview text
   */
  async previewCSV(transaction, maxLines = 5) {
    try {
      const validation = this.validateDownload(transaction);
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      let downloadUrl = transaction.downloadUrl;
      if (!downloadUrl && transaction.csvStoragePath) {
        const storageRef = ref(storage, transaction.csvStoragePath);
        downloadUrl = await getDownloadURL(storageRef);
      }

      const response = await fetch(downloadUrl);
      const text = await response.text();
      
      // Return first few lines
      const lines = text.split('\n').slice(0, maxLines);
      return lines.join('\n');

    } catch (error) {
      console.error('Preview error:', error);
      throw new Error('Failed to preview file');
    }
  }

  /**
   * Preview the filename that will be used for download
   * @param {string} pdfFileName - Original PDF filename
   * @returns {string} Preview of CSV filename
   */
  previewFilename(pdfFileName) {
    return this.generateCSVFileName(pdfFileName);
  }

  /**
   * Validate authentication before download
   * @param {Object} transaction - Transaction object
   * @returns {Promise<Object>} Validation result
   */
  async validateAuthForDownload(transaction) {
    try {
      // Check if user is authenticated
      const isAuthenticated = await isUserAuthenticated();
      if (!isAuthenticated) {
        return {
          valid: false,
          error: 'Please log in to download files. You may need to sign in again in your browser.'
        };
      }

      // Get current user
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        return {
          valid: false,
          error: 'Authentication session expired. Please log in again.'
        };
      }

      // Validate user owns this transaction
      if (transaction.userId && transaction.userId !== currentUser.uid) {
        return {
          valid: false,
          error: 'Access denied: You can only download your own files.'
        };
      }

      // Validate transaction is ready for download
      const downloadValidation = this.validateDownload(transaction);
      if (!downloadValidation.valid) {
        return downloadValidation;
      }

      // Try to refresh auth token to ensure it's valid
      try {
        await currentUser.getIdToken(true); // Force refresh
      } catch (tokenError) {
        return {
          valid: false,
          error: `Authentication token error: ${tokenError.message}. Please refresh the page and log in again.`
        };
      }

      return {
        valid: true,
        user: currentUser
      };

    } catch (error) {
      return {
        valid: false,
        error: `Authentication check failed: ${error.message}. Please try logging in again.`
      };
    }
  }

  /**
   * Handle authentication-related download errors
   * @param {Error} error - Original error
   * @returns {Error} User-friendly error
   */
  handleAuthError(error) {
    const message = error.message.toLowerCase();
    
    if (message.includes('unauthorized') || message.includes('permission') || message.includes('auth')) {
      return new Error('Authentication required. Please log in again and try downloading.');
    }
    
    if (message.includes('network') || message.includes('fetch')) {
      return new Error('Network error. Please check your internet connection and try again.');
    }
    
    return error; // Return original error if not auth-related
  }
}

// Export singleton instance
export const downloadManager = new DownloadManager();
export default downloadManager;